import type { MailboxInfo } from 'owa-client-types';
import { getGlobalSettingsAccountMailboxInfo } from 'owa-account-source-list-store';
import { isAccountFeatureEnabled, isGlobalFeatureEnabled } from 'owa-feature-flags';
import { isPstFileMailboxInfo } from 'owa-client-types/lib/PstFileMailboxInfo';
import { getAccountScopeUserSettings } from 'owa-session-store';
import type OutlookDataFileProperty from 'owa-service/lib/contract/OutlookDataFileProperty';
import { isGulpOrBranching } from 'owa-config';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { getWin32Policy } from 'owa-session-store/lib/utils/win32Policy';
import { type MoveCopyType } from './copyMoveItemUtils';

/**
 * Check if the mailboxes are supported for Move/Copy cross account
 * Covers the following scenarios:
 * - From PST to PST
 * - From online account to PST file
 * - From PST file to online account
 * - From online account to online account
 * @param sourceMailboxInfo Source mailbox info
 * @param destinationMailboxInfo Destination mailbox info
 */
export default function getMoveCopyCrossAccountType(
    sourceMailboxInfo: MailboxInfo,
    destinationMailboxInfo: MailboxInfo
): MoveCopyType {
    const isSourceMailboxPST = isPstFileMailboxInfo(sourceMailboxInfo);
    const isDestinationMailboxPST = isPstFileMailboxInfo(destinationMailboxInfo);

    // Scenario 1: From PST to PST - Checks if the PST feature is enabled
    if (isSourceMailboxPST && isDestinationMailboxPST) {
        // PST Flight have to be enabled for Global Account
        return isAccountFeatureEnabled(
            'acct-pstFileImportExport',
            getGlobalSettingsAccountMailboxInfo()
        )
            ? 3
            : 0;
    }

    /* Scenario 2: From online account to PST file
       Checks:
        - If the OutlookDataFile property is different than deny or noGrow for sourceMailboxInfo
        - If the PST flight is enabled for sourceMailboxInfo
    */
    if (!isSourceMailboxPST && isDestinationMailboxPST) {
        const outlookDataFilePolicy =
            getAccountScopeUserSettings(sourceMailboxInfo)?.PolicySettings?.OutlookDataFile;

        const isOutlookDataFileMoveAllowed =
            isAccountFeatureEnabled('acct-pstFileImportExport', sourceMailboxInfo) &&
            !getWin32Policy().DisablePst &&
            !getWin32Policy().PSTDisableGrow &&
            (outlookDataFilePolicy === 0 || outlookDataFilePolicy === 1);

        return isOutlookDataFileMoveAllowed ? 3 : 0;
    }

    /* Scenario 3: From PST file to online account
        Checks:
         - If the OutlookDataFile property is different than deny for destinationMailboxInfo
         - If the PST flight is enabled for destinationMailboxInfo
    */
    if (isSourceMailboxPST && !isDestinationMailboxPST) {
        const isOutlookDataFileMoveAllowed =
            isAccountFeatureEnabled('acct-pstFileImportExport', destinationMailboxInfo) &&
            !getWin32Policy().DisablePst &&
            getAccountScopeUserSettings(destinationMailboxInfo)?.PolicySettings?.OutlookDataFile !==
                5;

        return isOutlookDataFileMoveAllowed ? 3 : 0;
    }

    // We enable cross account move/copy for consumer accounts regardless of the policy settings.
    const isPolicyEnabledForSourceMailbox =
        isConsumer(undefined, sourceMailboxInfo) ||
        getAccountScopeUserSettings(sourceMailboxInfo)?.PolicySettings?.ItemsToOtherAccountsEnabled;
    const isPolicyEnabledForDestinationMailbox =
        isConsumer(undefined, destinationMailboxInfo) ||
        getAccountScopeUserSettings(destinationMailboxInfo)?.PolicySettings
            ?.ItemsToOtherAccountsEnabled;

    // If we are in Gulp or Branching and the policy override flight is enabled,
    // we allow cross account move/copy without checking policies so we can test with our mailboxes (Microsoft accounts)
    const satisfyPolicySettings =
        (isGlobalFeatureEnabled('mon-enableCrossAccountMoveCopyPolicyOverride') &&
            isGulpOrBranching()) ||
        (isPolicyEnabledForSourceMailbox && isPolicyEnabledForDestinationMailbox);

    if (!satisfyPolicySettings) {
        // If policies are not satisfied, we return NotAllowedPolicy
        return 4;
    }

    // Scenario 4: From online account to online account. Features and policies are checked for both mailboxes
    return 3;
}
